"use client";

import { useState } from "react";
import type { Question } from "@bash/shared";
import { cn } from "@/lib/utils";

export interface NeedCardAnswerProps {
  questions: Question[];
  busy?: boolean;
  onSubmit: (answers: Record<string, string>) => void;
  onCancel: () => void;
}

/**
 * Inline answer panel. SAME wire format as the in-task QuestionBlock:
 * answers keyed by the question text, multi-select joined with commas,
 * free text allowed alongside options. If the formats ever drift, the
 * sandbox resolver receives answers it can't attribute — don't drift.
 */
export function NeedCardAnswer({
  questions,
  busy,
  onSubmit,
  onCancel,
}: NeedCardAnswerProps) {
  const [picked, setPicked] = useState<Record<string, string[]>>({});
  const [text, setText] = useState<Record<string, string>>({});

  const toggle = (q: Question, label: string) => {
    setPicked((prev) => {
      const current = prev[q.question] ?? [];
      if (q.multiSelect) {
        return {
          ...prev,
          [q.question]: current.includes(label)
            ? current.filter((l) => l !== label)
            : [...current, label],
        };
      }
      return { ...prev, [q.question]: current[0] === label ? [] : [label] };
    });
  };

  const answerFor = (q: Question): string => {
    const free = (text[q.question] ?? "").trim();
    const chosen = picked[q.question] ?? [];
    if (chosen.length > 0)
      return free ? [...chosen, free].join(",") : chosen.join(",");
    return free;
  };

  const complete = questions.every((q) => answerFor(q).length > 0);

  return (
    <div className="mt-3 flex flex-col gap-2.5 mb-3">
      {questions.map((q, i) => (
        <div key={q.question} className="hb-need-answer__group">
          {(questions.length > 1 || i > 0) && (
            <div className="text-3.5 font-semibold text-fg-default">
              {q.question}
            </div>
          )}
          {q.options.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {q.options.map((o) => (
                <button
                  key={o.label}
                  type="button"
                  className={cn(
                    "text-[12.5px] font-medium py-1.5 px-2.75 rounded-full border border-border-muted bg-transparent text-fg-default cursor-pointer font-sans",
                    (picked[q.question] ?? []).includes(o.label)
                      ? "[border-color:color-mix(in_srgb,_var(--cc)_55%,_var(--border-muted))] bg-cc/10"
                      : ""
                  )}
                  title={o.description}
                  onClick={() => toggle(q, o.label)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}
          <input
            className="text-3.5 py-2 px-3 rounded-10 border border-border-muted bg-transparent text-fg-default w-full font-sans focus:outline-none focus:[border-color:color-mix(in_srgb,_var(--cc)_45%,_var(--border-muted))]"
            placeholder={
              q.options.length > 0 ? "Or type your own" : "Type your answer"
            }
            value={text[q.question] ?? ""}
            onChange={(e) =>
              setText((prev) => ({ ...prev, [q.question]: e.target.value }))
            }
          />
        </div>
      ))}
      <div className="flex gap-2">
        <button
          className="text-3.5 font-semibold rounded-10 py-2 px-3.5 border border-solid border-transparent cursor-pointer font-inter bg-fg-default text-bg-base"
          disabled={!complete || busy}
          onClick={() =>
            onSubmit(
              Object.fromEntries(
                questions.map((q) => [q.question, answerFor(q)])
              )
            )
          }
        >
          {busy ? "Sending…" : "Send answer"}
        </button>
        <button
          className="text-3.5 font-semibold rounded-10 py-2 px-3.5 border border-solid cursor-pointer font-inter bg-transparent text-fg-muted border-border-muted"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
